import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
export interface OriginRect {
  top: number;
  left: number;
  width: number;
  height: number;
}
interface VideoLightboxProps {
  open: boolean;
  youtubeId: string;
  previewSrc: string;
  previewPoster: string;
  getOrigin: () => OriginRect | null;
  onClose: () => void;
}
const EXPAND_EASE = [0.76, 0, 0.24, 1] as const;
const EXPAND_DURATION = 0.6;

// Clay-style expansion: the reel grows out of its exact on-page frame to fill the screen
// (clip-path from the frame's rect to the full viewport), then the YouTube player fades in.
// Closing collapses it back into the frame.
function clipFrom(rect: OriginRect | null) {
  if (!rect) return 'inset(0px 0px 0px 0px)';
  const right = window.innerWidth - (rect.left + rect.width);
  const bottom = window.innerHeight - (rect.top + rect.height);
  return `inset(${rect.top}px ${right}px ${bottom}px ${rect.left}px)`;
}
export function VideoLightbox({
  open,
  youtubeId,
  previewSrc,
  previewPoster,
  getOrigin,
  onClose
}: VideoLightboxProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);
  return <AnimatePresence data-id="element-181" data-mp="3v85tdaj13p7m">
      {open && <motion.div role="dialog" aria-modal="true" aria-label="Immersive Studios showreel" initial={{
      clipPath: clipFrom(getOrigin())
    }} animate={{
      clipPath: 'inset(0px 0px 0px 0px)'
    }} exit={{
      clipPath: clipFrom(getOrigin())
    }} transition={{
      duration: EXPAND_DURATION,
      ease: EXPAND_EASE
    }} className="fixed inset-0 z-50 bg-black" data-id="element-182" data-mp="3r85expvxje0k">
          {/* The muted preview fills the screen during the expansion, so the frame never goes blank */}
          <video className="absolute inset-0 h-full w-full object-cover opacity-70" src={previewSrc} poster={previewPoster} autoPlay muted loop playsInline aria-hidden="true" data-id="element-183" data-mp="2h9cfglhqlwam" />
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1,
        transition: {
          delay: EXPAND_DURATION,
          duration: 0.3,
          ease: 'easeOut'
        }
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.15,
          ease: 'easeOut'
        }
      }} className="absolute inset-0 flex items-center justify-center bg-black p-4 sm:p-10" onClick={onClose} data-id="element-184" data-mp="1tkt41x3e53eb">
            <div className="aspect-video w-full max-w-[calc((100vh-5rem)*16/9)]" onClick={e => e.stopPropagation()} data-id="element-185" data-mp="1alp1n76cu7vm">
              <iframe className="h-full w-full" src={`https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`} title="Immersive Studios showreel" allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen data-id="element-186" data-mp="1fka134ds8nj0" />
            </div>
          </motion.div>
          <motion.button ref={closeRef} type="button" onClick={onClose} aria-label="Close video" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1,
        transition: {
          delay: EXPAND_DURATION * 0.6,
          duration: 0.2,
          ease: 'easeOut'
        }
      }} exit={{
        opacity: 0,
        transition: {
          duration: 0.1
        }
      }} className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/15 text-white backdrop-blur-[20px] transition-colors duration-200 hover:bg-white/25" data-id="element-187" data-mp="10yurv5k56e8p">
            <XIcon className="h-5 w-5" data-id="element-188" data-mp="1sq2c41n7ce1z" />
          </motion.button>
        </motion.div>}
    </AnimatePresence>;
}